import { useState } from "react";
import { downloadOriginal } from "./StorageRecovery";
import { retryTossStorage } from "./platform/apps-in-toss";
import { canOpenStoredCopy, type TossStorageState } from "./platform/toss-storage";

function CopySummary({ raw }: { raw: string | null }) {
  if (raw === null || !canOpenStoredCopy(raw)) return null;
  const data = JSON.parse(raw) as { projects: Array<{ id: string; characterName: string; mediaTitle: string; tasks: Array<{ status: string }> }>; wardrobe?: unknown[]; photos?: unknown[] };
  const tasks = data.projects.flatMap((project) => project.tasks);
  return <div className="copy-summary">
    <p>준비표 {data.projects.length}개 · 확인한 할 일 {tasks.filter((task) => task.status === "done").length}/{tasks.length}개</p>
    <ul>{data.projects.slice(0, 3).map((project) => <li key={project.id}>{project.characterName} · {project.mediaTitle}</li>)}</ul>
    {data.projects.length > 3 && <p>그 외 준비표 {data.projects.length - 3}개</p>}
    <p>의상 {data.wardrobe?.length ?? 0}개 · 사진 기록 {data.photos?.length ?? 0}개</p>
  </div>;
}

export function TossStorageRecovery({ state, resolving, resolve }: {
  state: TossStorageState;
  resolving: boolean;
  resolve: (choice: "local" | "toss" | "empty") => Promise<boolean>;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const choose = async (choice: "local" | "toss" | "empty") => {
    setError("");
    if (!await resolve(choice)) setError("사본이 바뀌었거나 원본 보관을 확인하지 못했어요. 두 기록을 다시 확인하고 재시도해 주세요.");
  };
  if (resolving) return <section className="storage-recovery" role="status">양쪽 원본을 따로 보관하고 선택한 준비표를 확인하고 있어요…</section>;
  if (state.status === "web" || state.status === "synced") return null;
  if (state.status !== "conflict") return <section className="storage-recovery" role="status">
    <h2>{state.status === "error" ? "토스 보관을 확인하지 못했어요" : "토스에 기록을 보관하는 중이에요"}</h2>
    <p>지금 화면의 기록과 토스에 보관된 기록이 같은지 확인 중이에요. 확인이 끝나기 전에는 기기 저장 공간을 지우지 말아 주세요.</p>
    {state.status === "error" && <button type="button" className="button" onClick={() => void retryTossStorage()}>토스 보관 다시 시도</button>}
  </section>;
  return <section className="storage-recovery" aria-labelledby="toss-conflict-title">
    <h1 id="toss-conflict-title">기기와 토스에 서로 다른 준비표가 있어요</h1>
    <p>어느 쪽이 최신인지 알 수 없어 자동 저장을 멈췄어요. 사용할 기록을 골라 주세요. 양쪽 원본을 기기와 토스에 따로 보관한 뒤 선택을 적용해요.</p>
    <label className="check-label"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> 원본을 따로 보관한 뒤 선택한 준비표를 여는 것을 확인했어요</label>
    <div className="conflict-copies">{([["local", "현재 기기", state.localRaw], ["toss", "토스 보관함", state.durableRaw]] as const).map(([choice, label, raw]) => <article key={choice}>
      <h2>{label}</h2><p>{raw === null ? "저장된 기록 없음" : canOpenStoredCopy(raw) ? "열 수 있는 준비표" : "현재 버전에서 열 수 없는 기록"}</p>
      <CopySummary raw={raw} />
      {raw !== null && <><details><summary>{label} 원본 텍스트 보기</summary><textarea aria-label={`${label} 원본 텍스트`} readOnly value={raw} rows={6} /></details><button type="button" className="button" onClick={() => downloadOriginal(raw, `haebolcae-${choice}-original.txt`)}>{label} 원본 받기</button></>}
      <button type="button" className="button primary" disabled={!confirmed || !canOpenStoredCopy(raw)} onClick={() => void choose(choice)}>{label} 기록 사용</button>
    </article>)}</div>
    <div className="data-button-row"><button type="button" className="button" onClick={() => void retryTossStorage()}>두 기록 다시 확인</button><button type="button" className="button" disabled={!confirmed} onClick={() => void choose("empty")}>양쪽 보관 후 빈 준비표 열기</button></div>
    {error && <p role="alert">{error}</p>}
  </section>;
}
